import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { HeadlineTestMetrics } from '@/services/headlineTestService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Activity, Trophy, LineChart as LineChartIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeadlineRetentionChartProps {
  headlineOverlay: HeadlineOverlay;
  metricsData?: HeadlineTestMetrics[] | null;
}

const PALETTE = ['#1F6BFF', '#94B8FF', '#6B9DFF', '#C8D9FF', '#B0C9FF'];

type RetentionBucket = { count: number; percent: number | null };
type RetentionByTime = Record<string, RetentionBucket>;

export const HeadlineRetentionChart: React.FC<HeadlineRetentionChartProps> = ({
  headlineOverlay,
  metricsData,
}) => {
  const _interfaceLanguage = useLanguage();
  const [selectedVariation, setSelectedVariation] = useState<string>('all');

  // Extrai as variações com dados reais da API. Cada item traz
  // `metric.retencao` no formato { "mm:ss": { count, percent } }.
  const variations = useMemo(() => {
    if (!metricsData?.length) return [];
    return metricsData
      .map((item, idx) => {
        const retencao: RetentionByTime | undefined =
          (item?.metric as any)?.retencao ?? (item as any)?.retencao;
        if (!retencao || typeof retencao !== 'object') return null;

        const hasRealData = Object.values(retencao).some(
          (v) => v && v.percent !== null && v.percent !== undefined,
        );
        if (!hasRealData) return null;

        return {
          label:
            (item.headlineTest as any)?.nomeVideo || translateInterfaceText("Variação {value0}", { value0: (idx + 1) }),
          retencao,
        };
      })
      .filter((v): v is { label: string; retencao: RetentionByTime } => v !== null);
  }, [metricsData, _interfaceLanguage]);

  const variationOptions = useMemo(
    () =>
      variations.map((v, i) => ({
        value: `var${i}`,
        label: v.label,
      })),
    [variations],
  );

  const chartData = useMemo(() => {
    if (variations.length === 0) return [] as Array<Record<string, number | string>>;

    // Junta todos os pontos de tempo de todas as variações.
    const allTimes = new Set<string>();
    variations.forEach((v) => Object.keys(v.retencao).forEach((t) => allTimes.add(t)));

    const sortedTimes = Array.from(allTimes).sort((a, b) => {
      const [am, as] = a.split(':').map(Number);
      const [bm, bs] = b.split(':').map(Number);
      return am * 60 + as - (bm * 60 + bs);
    });

    return sortedTimes.map((time) => {
      const row: Record<string, number | string> = { time };
      variations.forEach((v) => {
        row[v.label] = v.retencao[time]?.percent ?? 0;
      });
      return row;
    });
  }, [variations]);

  // Variação "vencedora" = maior retenção no último ponto válido.
  const winner = useMemo(() => {
    if (chartData.length === 0) return undefined;
    const last = chartData[chartData.length - 1];
    let best = { label: variations[0]?.label, value: -Infinity };
    variations.forEach((v) => {
      const val = Number(last[v.label] ?? 0);
      if (val > best.value) best = { label: v.label, value: val };
    });
    return best.label;
  }, [chartData, variations]);

  const isTestEnabled = headlineOverlay.abTestConfig?.enabled;
  const configVariations = headlineOverlay.abTestConfig?.variations || [];

  // Empty states
  if (!isTestEnabled || configVariations.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-12 shadow-[0_2px_12px_rgba(0,0,0,0.04)] text-center">
        <div className="w-16 h-16 bg-muted/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <LineChartIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{translateInterfaceText("Nenhum teste ativo")}</h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          {translateInterfaceText("Configure um teste na aba \"Teste A/B\" para visualizar curvas de retenção.")}</p>
      </div>
    );
  }

  if (variations.length === 0 || chartData.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-12 shadow-[0_2px_12px_rgba(0,0,0,0.04)] text-center">
        <div className="w-16 h-16 bg-muted/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <LineChartIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{translateInterfaceText("Sem dados de retenção")}</h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          {translateInterfaceText("Ainda não há dados de retenção para as variações deste teste. Assim que forem coletados, a curva comparativa aparecerá aqui.")}</p>
      </div>
    );
  }

  const isVisible = (label: string) =>
    selectedVariation === 'all' ||
    variationOptions.find((v) => v.value === selectedVariation)?.label === label;

  const lineOpacity = (label: string) =>
    selectedVariation !== 'all' ? 1 : label === winner ? 1 : 0.55;

  const lineWidth = (label: string) => (label === winner ? 2.6 : 1.8);

  const finalRetention = Number(chartData[chartData.length - 1]?.[winner ?? ''] ?? 0);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#EBEEF2] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-[#0F172A] leading-tight">
                {translateInterfaceText("Análise de Retenção")}</h3>
              <p className="text-[12px] text-[#6B7280] mt-1 max-w-[420px] leading-relaxed">
                {translateInterfaceText("Compare como cada variação influencia a permanência da audiência ao longo do vídeo.")}</p>
            </div>
            <Select value={selectedVariation} onValueChange={setSelectedVariation}>
              <SelectTrigger className="h-8 w-[160px] rounded-lg border-[#E6EAF2] bg-white text-[12.5px] font-medium text-[#374151] shadow-none outline-none ring-0 focus:border-[#E6EAF2] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none">
                <SelectValue placeholder={translateInterfaceText("Filtrar")} />
              </SelectTrigger>
              <SelectContent className="border-[#E6EAF2]">
                <SelectItem value="all">{translateInterfaceText("Todas")}</SelectItem>
                {variationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Micro insights */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-[#EBEEF2] bg-[#FAFBFD] px-3 py-2 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                <Trophy className="h-3 w-3 text-[#1F6BFF] shrink-0" />
                {translateInterfaceText("Melhor")}</div>
              <div className="text-[14px] font-semibold text-[#0F172A] mt-0.5 truncate">
                {winner || '—'}
              </div>
            </div>
            <div className="rounded-lg border border-[#EBEEF2] bg-[#FAFBFD] px-3 py-2 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                <Activity className="h-3 w-3 text-[#1F6BFF] shrink-0" />
                {translateInterfaceText("Retenção")}</div>
              <div className="text-[14px] font-semibold text-[#0F172A] mt-0.5">
                {finalRetention.toFixed(1)}%
              </div>
            </div>
            <div className="rounded-lg border border-[#EBEEF2] bg-[#FAFBFD] px-3 py-2 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                <TrendingUp className="h-3 w-3 text-[#1F6BFF] shrink-0" />
                {translateInterfaceText("Queda")}</div>
              <div className="text-[14px] font-semibold text-[#0F172A] mt-0.5 truncate">
                {winner || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="px-3 pb-3">
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#EEF1F6" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="time"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#E6EAF2' }}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                />
                <Tooltip
                  cursor={{ stroke: '#1F6BFF', strokeWidth: 1, strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="rounded-lg border border-[#E6EAF2] bg-white/95 backdrop-blur px-3 py-2 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
                        <p className="text-[11px] font-semibold text-[#0F172A] mb-1.5">
                          {translateInterfaceText("Tempo:")}{" "}{payload[0].payload.time}
                        </p>
                        <div className="space-y-1">
                          {payload
                            .sort((a: any, b: any) => (b.value as number) - (a.value as number))
                            .map((entry: any, i: number) => (
                              <div key={i} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-[11px] text-[#6B7280] truncate max-w-[140px]">
                                    {entry.dataKey}
                                    {entry.dataKey === winner && translateInterfaceText(" · vencedora")}
                                  </span>
                                </div>
                                <span className="text-[11.5px] font-semibold text-[#0F172A] tabular-nums">
                                  {Number(entry.value).toFixed(1)}%
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  }}
                />
                {variationOptions.map((v, i) =>
                  isVisible(v.label) ? (
                    <Line
                      key={v.value}
                      type="monotone"
                      dataKey={v.label}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={lineWidth(v.label)}
                      strokeOpacity={lineOpacity(v.label)}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                    />
                  ) : null,
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legenda */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 pt-2 pb-1">
            {variationOptions.map((v, i) => (
              <div key={v.value} className="flex items-center gap-1.5">
                <span
                  className={cn('inline-block h-[3px] w-4 rounded-full')}
                  style={{
                    backgroundColor: PALETTE[i % PALETTE.length],
                    opacity: lineOpacity(v.label),
                  }}
                />
                <span className="text-[11.5px] font-medium text-[#374151] truncate max-w-[120px]">
                  {v.label}
                </span>
                {v.label === winner && (
                  <span className="ml-0.5 inline-flex items-center rounded-full bg-[#EEF4FF] px-1.5 py-[1px] text-[9.5px] font-semibold text-[#1F6BFF]">
                    {translateInterfaceText("Vencedora")}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
